<div class="add-product-container">
  <!-- Error Notification -->
  <div *ngIf="errorMessage" class="error-notification">
    <svg class="error-notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#dc2626"/>
      <path d="M15 9L9 15M9 9L15 15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <span class="error-notification-message">{{ errorMessage }}</span>
  </div>

  <!-- Success Notification -->
  <div *ngIf="successMessage" class="success-notification">
    <svg class="success-notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#16a34a"/>
      <path d="M9 12L11 14L15 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <span class="success-notification-message">{{ successMessage }}</span>
  </div>

  <div class="page-header">
    <h2>Add Product</h2>
  </div>

  <!-- Tabs -->
  <div class="tabs-container">
    <div class="tabs">
      <button 
        class="tab-button" 
        [class.active]="activeTab === 'details'"
        (click)="switchTab('details')">
        Details
      </button>
      <button 
        class="tab-button" 
        [class.active]="activeTab === 'requirements'"
        (click)="switchTab('requirements')">
        Requirements
      </button>
    </div>
  </div>

  <!-- Details Tab -->
  <div class="tab-content" *ngIf="activeTab === 'details'">
    <form [formGroup]="detailsForm" class="product-form">
      <!-- Product Category and Product Name in same row -->
      <div class="form-row">
        <!-- Product Category -->
        <div class="form-group">
          <label>Product Category <span class="required">*</span></label>
          <select 
            formControlName="category" 
            class="form-control"
            [class.invalid]="(detailsForm.get('category')?.invalid && detailsForm.get('category')?.touched) || (!categoryHasSuppliers && selectedCategory)">
            <option value="">Select Product Category</option>
            <option *ngFor="let cat of categories" [value]="cat._id">{{ cat.name }}</option>
          </select>
          <div *ngIf="detailsForm.get('category')?.invalid && detailsForm.get('category')?.touched" class="error-text">
            Category is required
          </div>
          <div *ngIf="!categoryHasSuppliers && selectedCategory && categorySupplierWarning" class="error-text" style="color: #dc2626; margin-top: 5px;">
            {{ categorySupplierWarning }}
          </div>
        </div>

        <!-- Product Name -->
        <div class="form-group">
          <label>Product Name <span class="required">*</span></label>
          <input 
            type="text" 
            formControlName="name" 
            class="form-control"
            placeholder="Enter Product Name"
            (blur)="checkProductName()"
            (input)="onProductNameInput()"
            [class.invalid]="(detailsForm.get('name')?.invalid || !productNameAvailable) && detailsForm.get('name')?.touched">
          <div *ngIf="checkingName" class="checking-text">Checking availability...</div>
          <div *ngIf="!checkingName && !productNameAvailable && detailsForm.get('name')?.touched" class="error-text">
            Product name already exists
          </div>
          <div *ngIf="detailsForm.get('name')?.invalid && detailsForm.get('name')?.touched && productNameAvailable" class="error-text">
            Product name is required
          </div>
        </div>
      </div>

      <!-- Upload Product Image -->
      <div class="form-group">
        <label>Upload Product Image</label>
        <div class="image-upload-wrapper">
          <div class="image-upload-left">
            <div class="file-input-wrapper">
              <input type="file" #productImageInput class="file-input" accept="image/jpeg,image/jpg,image/png" (change)="onProductImageSelected($event)" style="display: none;">
              <input type="text" 
                     class="form-control file-display" 
                     [value]="selectedProductImageFile?.name || 'Select File'" 
                     placeholder="Select File" 
                     readonly 
                     (click)="productImageInput.click()">
              <button type="button" class="btn btn-upload" (click)="productImageInput.click(); $event.preventDefault()">Upload</button>
            </div>
            <small class="file-hint">(File format either jpg, jpeg or png. Max file size 10 MB)</small>
            <div class="upload-status" *ngIf="productImageUploading">
              <span class="uploading-text">Uploading image...</span>
            </div>
            <div class="upload-error" *ngIf="productImageUploadError">
              <span class="error-text">{{ productImageUploadError }}</span>
            </div>
            <div class="upload-success" *ngIf="uploadedProductImageUrl && !productImageUploading && !productImageUploadError">
              <span class="success-text">✓ Image uploaded successfully</span>
            </div>
          </div>
          <div class="image-upload-right">
            <div class="image-preview-container" *ngIf="productImagePreview || uploadedProductImageUrl">
              <img [src]="productImagePreview || uploadedProductImageUrl" alt="Preview" class="image-preview">
              <button type="button" class="btn-remove-image" (click)="removeProductImage()" title="Remove image">×</button>
            </div>
            <!-- <div class="no-image-placeholder" *ngIf="!productImagePreview && !uploadedProductImageUrl && !productImageUploading">
              <div class="placeholder-content">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="60" height="60" fill="#e5e7eb" rx="4"/>
                  <path d="M30 18L37.5 27H22.5L30 18Z" fill="#9ca3af"/>
                  <rect x="15" y="30" width="30" height="18" fill="#9ca3af"/>
                </svg>
                <span class="placeholder-text">No Image</span>
              </div>
            </div> -->
          </div>
        </div>
      </div>

      <!-- Specifications Section -->
      <div class="specifications-section">
        <h4>Specifications</h4>
        <div formArrayName="specifications">
          <div 
            *ngFor="let spec of specificationsControls; let i = index" 
            [formGroupName]="i"
            class="specification-row">
            
            <div class="spec-field">
              <label>Key Type</label>
              <select formControlName="keyType" class="form-control">
                <option *ngFor="let type of keyTypes" [value]="type">{{ type }}</option>
              </select>
            </div>

            <div class="spec-field">
              <label>Key <span class="required">*</span></label>
              <input 
                type="text" 
                formControlName="key" 
                class="form-control"
                placeholder="Enter Key">
            </div>

            <div class="spec-field spec-value-field">
              <label>Value <span class="required">*</span></label>
              
              <!-- Text Field -->
              <input 
                *ngIf="spec.get('keyType')?.value === 'Text Field'"
                type="text" 
                formControlName="value" 
                class="form-control"
                placeholder="Enter Value">

              <!-- Text Area -->
              <textarea 
                *ngIf="spec.get('keyType')?.value === 'Text Area'"
                formControlName="value" 
                class="form-control"
                rows="3"
                placeholder="Enter Value"></textarea>

              <!-- Bullet Point -->
              <textarea 
                *ngIf="spec.get('keyType')?.value === 'Bullet Point'"
                formControlName="value" 
                class="form-control"
                rows="3"
                placeholder="Enter each point on a new line"></textarea>

              <!-- Link -->
              <input 
                *ngIf="spec.get('keyType')?.value === 'Link'"
                type="text" 
                formControlName="value" 
                class="form-control"
                placeholder="Enter URL">

              <!-- Upload -->
              <div *ngIf="spec.get('keyType')?.value === 'Upload'" class="upload-field">
                <div class="file-input-wrapper">
                  <input 
                    type="text" 
                    formControlName="value" 
                    class="form-control file-display"
                    [value]="specificationFileUploads[i].fileName || 'Select File'"
                    placeholder="Select File"
                    readonly
                    (click)="triggerFileInput(i)">
                  <input 
                    type="file" 
                    [id]="'specFileInput' + i"
                    class="file-input"
                    (change)="onFileSelected($event, i)"
                    accept=".ai,.cdr,.pdf,.png,.jpeg,.jpg,.xlsx,.xls,.csv"
                    style="display: none;">
                  <button 
                    type="button" 
                    class="btn-upload"
                    (click)="triggerFileInput(i); $event.preventDefault()"
                    [disabled]="specificationFileUploads[i].uploading">
                    <span *ngIf="!specificationFileUploads[i]?.uploading">Upload</span>
                    <span *ngIf="specificationFileUploads[i]?.uploading">Uploading...</span>
                  </button>
                </div>
                <div class="file-hint">
                  (File format either ai, cdr, pdf, png, jpeg, jpg, xlsx, xls or csv. Max file size 150 MB)
                </div>
                <!-- Upload Status -->
                <div class="upload-status" *ngIf="specificationFileUploads[i]">
                  <div class="upload-status-uploading" *ngIf="specificationFileUploads[i].uploading">
                    <span class="uploading-text">⏳ Uploading file...</span>
                  </div>
                  <div class="upload-status-success" *ngIf="specificationFileUploads[i].uploaded && !specificationFileUploads[i].uploading && !specificationFileUploads[i].error">
                    <span class="success-text">✓ File uploaded successfully</span>
                    <button 
                      type="button" 
                      class="btn-remove-file"
                      (click)="clearSpecificationFile(i)"
                      title="Remove file">
                      ×
                    </button>
                  </div>
                  <div class="upload-status-error" *ngIf="specificationFileUploads[i].error">
                    <span class="error-text">{{ specificationFileUploads[i].error }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="spec-actions">
              <button 
                type="button" 
                class="btn-icon btn-delete"
                (click)="removeSpecification(i)"
                *ngIf="specificationsControls.length > 1">
                🗑️
              </button>
              <button 
                type="button" 
                class="btn-icon btn-add"
                (click)="addSpecification()"
                *ngIf="i === specificationsControls.length - 1">
                ➕
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Next Button -->
      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="cancel()">Cancel</button>
        <button 
        type="button" 
        class="btn btn-primary"
        (click)="switchTab('requirements')"
        [disabled]="detailsForm.invalid || !productNameAvailable || checkingName || productImageUploading">
        Next
      </button>
      </div>
    </form>
  </div>

  <!-- Requirements Tab -->
  <div class="tab-content" *ngIf="activeTab === 'requirements'">
    <form [formGroup]="requirementsForm" class="product-form">
      <!-- Quote Submission Due Date -->
      <div class="form-row">
        <div class="form-group">
          <label>Quote Submission Due Date <span class="required">*</span></label>
          <input 
            type="date" 
            formControlName="quoteSubmissionDueDate" 
            class="form-control date-input"
            [min]="getTodayDate()"
            [class.invalid]="requirementsForm.get('quoteSubmissionDueDate')?.invalid && requirementsForm.get('quoteSubmissionDueDate')?.touched"
            (click)="openDatePicker($event)"
            (focus)="openDatePicker($event)"
            (keydown)="preventManualDateInput($event)"
            (paste)="$event.preventDefault()">
          <div *ngIf="requirementsForm.get('quoteSubmissionDueDate')?.invalid && requirementsForm.get('quoteSubmissionDueDate')?.touched" class="error-text">
            Due date is required
          </div>
        </div>

        <!-- Shipping Term -->
        <div class="form-group">
          <label>Shipping Term <span class="required">*</span></label>
          <select 
            formControlName="shippingTerm" 
            class="form-control"
            [class.invalid]="requirementsForm.get('shippingTerm')?.invalid && requirementsForm.get('shippingTerm')?.touched">
            <option value="">Select Shipping Term</option>
            <option *ngFor="let term of shippingTerms" [value]="term">{{ term }}</option>
          </select>
          <div *ngIf="requirementsForm.get('shippingTerm')?.invalid && requirementsForm.get('shippingTerm')?.touched" class="error-text">
            Shipping term is required
          </div>
        </div>
      </div>

      <!-- Shipping Details - Delivered -->
      <div *ngIf="requirementsForm.get('shippingTerm')?.value === 'DELIVERED'" class="shipping-section">
        <h4>Shipping Details</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Address Line 1 <span class="required">*</span></label>
            <input 
              type="text" 
              formControlName="addressLine1" 
              class="form-control"
              placeholder="Enter Address" [disabled]="true">
          </div>
          <div class="form-group">
            <label>Address Line 2</label>
            <input 
              type="text" 
              formControlName="addressLine2" 
              class="form-control"
              placeholder="Enter Address" [disabled]="true">
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Country <span class="required">*</span></label>
            <input 
              type="text" 
              formControlName="country" 
              class="form-control"
              readonly
              [disabled]="true">
          </div>
          <div class="form-group">
            <label>State <span class="required">*</span></label>
            <select 
              formControlName="state" 
              class="form-control"
              [class.invalid]="requirementsForm.get('state')?.invalid && requirementsForm.get('state')?.touched">
              <option value="">Select State</option>
              <option *ngFor="let state of indianStates" [value]="state">{{ state }}</option>
            </select>
            <div *ngIf="requirementsForm.get('state')?.invalid && requirementsForm.get('state')?.touched" class="error-text">
              State is required
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>City <span class="required">*</span></label>
            <input 
              type="text" 
              formControlName="city" 
              class="form-control"
              placeholder="Enter City" [disabled]="true">
          </div>
          <div class="form-group">
            <label>Pincode <span class="required">*</span></label>
            <input 
              type="text" 
              formControlName="pincode" 
              class="form-control"
              placeholder="Enter Pincode" [disabled]="true">
          </div>
        </div>
      </div>

      <!-- Shipping Details - FOB -->
      <div *ngIf="requirementsForm.get('shippingTerm')?.value === 'FOB'" class="shipping-section">
        <h4>Shipping Details</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Country <span class="required">*</span></label>
            <input 
              type="text" 
              formControlName="country" 
              class="form-control"
              readonly
              [attr.disabled]="true">
          </div>
          <div class="form-group">
            <label>Mapped Port <span class="required">*</span> (Port of Loading)</label>
            <input 
              type="text" 
              formControlName="mappedPort" 
              class="form-control"
              placeholder="Enter Port"
              [class.invalid]="requirementsForm.get('mappedPort')?.invalid && requirementsForm.get('mappedPort')?.touched">
            <div *ngIf="requirementsForm.get('mappedPort')?.invalid && requirementsForm.get('mappedPort')?.touched" class="error-text">
              Mapped Port is required
            </div>
          </div>
        </div>
      </div>

      <!-- Shipping Details - CIF -->
      <div *ngIf="requirementsForm.get('shippingTerm')?.value === 'CIF'" class="shipping-section">
        <h4>Shipping Details</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Country <span class="required">*</span></label>
            <input 
              type="text" 
              formControlName="country" 
              class="form-control"
              readonly
              [attr.disabled]="true">
          </div>
          <div class="form-group">
            <label>Mapped Port <span class="required">*</span> (Port of Discharge)</label>
            <input 
              type="text" 
              formControlName="mappedPort" 
              class="form-control"
              placeholder="Enter Port"
              [class.invalid]="requirementsForm.get('mappedPort')?.invalid && requirementsForm.get('mappedPort')?.touched">
            <div *ngIf="requirementsForm.get('mappedPort')?.invalid && requirementsForm.get('mappedPort')?.touched" class="error-text">
              Mapped Port is required
            </div>
          </div>
        </div>
      </div>

      <!-- Quantity for Quote -->
      <div class="quantities-section">
        <h4>Quantity for Quote</h4>
        <div class="quantities-table">
          <div class="table-header">
            <div class="table-col">Quantity <span class="required">*</span></div>
            <div class="table-col">UOM <span class="required">*</span></div>
            <div class="table-col">Is Mandatory ?</div>
            <div class="table-col actions-col"></div>
          </div>
          <div formArrayName="quantities">
            <div 
              *ngFor="let qty of quantitiesControls; let i = index" 
              [formGroupName]="i"
              class="table-row">
              <div class="table-col">
                <input 
                  type="number" 
                  formControlName="quantity" 
                  class="form-control"
                  placeholder="Enter Quantity"
                  min="1">
              </div>
              <div class="table-col">
                <input 
                  type="text" 
                  formControlName="uom" 
                  class="form-control"
                  placeholder="Enter UOM">
              </div>
              <div class="table-col">
                <input 
                  type="checkbox" 
                  formControlName="isMandatory">
              </div>
              <div class="table-col actions-col">
                <button 
                  type="button" 
                  class="btn-icon btn-delete"
                  (click)="removeQuantity(i)"
                  *ngIf="quantitiesControls.length > 1">
                  🗑️
                </button>
                <button 
                  type="button" 
                  class="btn-icon btn-add"
                  (click)="addQuantity()"
                  *ngIf="i === quantitiesControls.length - 1">
                  ➕
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="form-actions">
        <button 
          type="button" 
          class="btn btn-secondary" 
          (click)="switchTab('details')"
          *ngIf="!loading">
          Back
        </button>
        <button 
          type="button" 
          class="btn btn-primary"
          (click)="submitProduct()"
          [disabled]="loading || detailsForm.invalid || requirementsForm.invalid">
          <span *ngIf="loading" class="submit-loading">
            <span class="loader-spinner-small"></span>
            Submitting...
          </span>
          <span *ngIf="!loading">Submit</span>
        </button>
       
        <!-- <button type="button" class="btn btn-secondary" (click)="cancel()">Cancel</button> -->
      </div>

      <!-- Full Screen Loader Overlay -->
      <div class="submit-loader-overlay" *ngIf="loading">
        <div class="submit-loader-content">
          <div class="loader-spinner-large"></div>
          <p class="loader-text">Creating product...</p>
        </div>
      </div>
    </form>
  </div>
</div>

